var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { syntheticEvent } from '../../components/helpers';
import { getState, escHtml, formatTableCell, sortTableRows, filterTableRows, absPosition, bindDelegated, } from './shared';
function resolveTableState(node) {
    var _a;
    var widget = (_a = node.metadata) === null || _a === void 0 ? void 0 : _a.widgetState;
    if (widget && typeof widget === 'object')
        return widget;
    return getState(node);
}
function patchTableState(node, patch) {
    var _a;
    if (((_a = node.metadata) === null || _a === void 0 ? void 0 : _a.widgetType) === 'dataTable') {
        node.metadata.widgetState = __assign(__assign({}, resolveTableState(node)), patch);
        return;
    }
    node.metadata.componentState = __assign(__assign({}, getState(node)), patch);
}
/** Data table with striped rows + optional search */
export function syncNativeTable(node, parent, ctx) {
    var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k;
    var state = resolveTableState(node);
    var columns = (_a = state.columns) !== null && _a !== void 0 ? _a : ['Name', 'Value'];
    var rows = (_b = state.rows) !== null && _b !== void 0 ? _b : [['A', '1']];
    var selectedRow = Number((_c = state.selectedRow) !== null && _c !== void 0 ? _c : -1);
    var colW = Number((_d = state.colWidth) !== null && _d !== void 0 ? _d : 100);
    var width = Number((_e = state.width) !== null && _e !== void 0 ? _e : colW * columns.length);
    var sortable = Boolean(state.sortable);
    var sortColumn = Number((_f = state.sortColumn) !== null && _f !== void 0 ? _f : -1);
    var sortDirection = String((_g = state.sortDirection) !== null && _g !== void 0 ? _g : 'asc') === 'desc' ? 'desc' : 'asc';
    var stickyHeader = state.stickyHeader !== false;
    var maxHeight = Number((_h = state.maxHeight) !== null && _h !== void 0 ? _h : 0);
    var showSearch = Boolean(state.showSearch);
    var search = String((_k = (_j = state.search) !== null && _j !== void 0 ? _j : state.searchQuery) !== null && _k !== void 0 ? _k : '');
    var striped = state.striped !== false;
    var displayRows = filterTableRows(rows, search);
    if (sortColumn >= 0 && sortColumn < columns.length) {
        displayRows = sortTableRows(displayRows, sortColumn, sortDirection);
    }
    var el = ctx.nodeElements.get(node.id);
    if (!el) {
        el = document.createElement('div');
        el.id = node.id;
        el.className = 'lightdraw-table-wrap lightdraw-table-wrap--scroll-x';
        el.setAttribute('role', 'grid');
        parent.appendChild(el);
        bindDelegated(el, function (e) {
            var _a, _b, _c, _d, _e;
            var th = e.target.closest('.lightdraw-table-th--sortable');
            if (th) {
                var ci = Number(th.getAttribute('data-col'));
                var st = resolveTableState(node);
                var prevCol = Number((_a = st.sortColumn) !== null && _a !== void 0 ? _a : -1);
                var prevDir = String((_b = st.sortDirection) !== null && _b !== void 0 ? _b : 'asc');
                var nextDir = prevCol === ci && prevDir === 'asc' ? 'desc' : 'asc';
                patchTableState(node, { sortColumn: ci, sortDirection: nextDir });
                node.emit('change', syntheticEvent('change', node, { value: ci, field: nextDir }));
                if (typeof node.metadata.chartRebuild === 'function') {
                    node.metadata.chartRebuild();
                }
                else {
                    (_c = node.getApp()) === null || _c === void 0 ? void 0 : _c.requestRender();
                }
                return;
            }
            var row = e.target.closest('.lightdraw-table-row');
            if (!row)
                return;
            var ri = Number(row.getAttribute('data-index'));
            var tableRows = (_d = resolveTableState(node).rows) !== null && _d !== void 0 ? _d : [];
            patchTableState(node, { selectedRow: ri });
            node.emit('select', syntheticEvent('select', node, { index: ri, row: tableRows[ri] }));
            if (typeof node.metadata.chartRebuild === 'function') {
                node.metadata.chartRebuild();
            }
            else {
                (_e = node.getApp()) === null || _e === void 0 ? void 0 : _e.requestRender();
            }
        });
        el.addEventListener('input', function (e) {
            var _a;
            var input = e.target.closest('.lightdraw-table-search');
            if (!input || !(input instanceof HTMLInputElement))
                return;
            var next = input.value;
            patchTableState(node, { search: next, searchQuery: next });
            if (typeof node.metadata.chartRebuild === 'function') {
                node.metadata.chartRebuild();
            }
            else {
                (_a = node.getApp()) === null || _a === void 0 ? void 0 : _a.requestRender();
            }
        });
        ctx.nodeElements.set(node.id, el);
    }
    else if (el.parentElement !== parent) {
        parent.appendChild(el);
    }
    var head = columns
        .map(function (c, ci) {
        var cls = 'lightdraw-table-th';
        if (sortable)
            cls += ' lightdraw-table-th--sortable';
        if (sortColumn === ci) {
            cls += sortDirection === 'asc' ? ' lightdraw-table-th--sorted-asc' : ' lightdraw-table-th--sorted-desc';
        }
        var sortIcon = sortable ? '<span class="lightdraw-table-sort-icon" aria-hidden="true"></span>' : '';
        return "<th scope=\"col\" class=\"".concat(cls, "\" data-col=\"").concat(ci, "\"><span class=\"lightdraw-table-th-label\">").concat(escHtml(c), "</span>").concat(sortIcon, "</th>");
    })
        .join('');
    var body = displayRows
        .map(function (row, ri) {
        var sourceIndex = rows.findIndex(function (r) { return r === row || r.every(function (c, i) { return c === row[i]; }); });
        var dataIndex = sourceIndex >= 0 ? sourceIndex : ri;
        var zebraCls = striped ? '' : ' lightdraw-table-row--no-stripe';
        return "<tr class=\"lightdraw-table-row".concat(zebraCls).concat(dataIndex === selectedRow ? ' lightdraw-table-row--selected' : '', "\" data-index=\"").concat(dataIndex, "\">").concat(row.map(function (cell) { return "<td>".concat(formatTableCell(cell), "</td>"); }).join(''), "</tr>");
    })
        .join('');
    var theadAttr = stickyHeader ? ' class="lightdraw-table-head--sticky"' : '';
    var scrollStyle = maxHeight > 0 ? " style=\"max-height:".concat(maxHeight, "px\"") : '';
    var searchHtml = showSearch
        ? "<div class=\"lightdraw-table-search-wrap\"><input type=\"search\" class=\"lightdraw-table-search\" placeholder=\"Search\u2026\" value=\"".concat(escHtml(search), "\" aria-label=\"Filter table rows\"></div>")
        : '';
    el.className = "lightdraw-table-wrap lightdraw-table-wrap--scroll-x".concat(striped ? '' : ' lightdraw-table-wrap--no-stripe');
    el.innerHTML = "".concat(searchHtml, "<div class=\"lightdraw-table-scroll\"").concat(scrollStyle, "><table class=\"lightdraw-table\"><thead").concat(theadAttr, "><tr>").concat(head, "</tr></thead><tbody>").concat(body, "</tbody></table></div>");
    var tableH = maxHeight > 0 ? maxHeight : 36 * (displayRows.length + 1) + (showSearch ? 40 : 0);
    el.style.cssText = absPosition(node, width, tableH);
    ctx.applyA11y(node, el);
    ctx.applyUiClasses(node, el);
    ctx.seenIds.add(node.id);
}
/** Expandable tree */
export function syncNativeTree(node, parent, ctx) {
    var _a, _b, _c, _d;
    var state = getState(node);
    var nodes = (_a = state.nodes) !== null && _a !== void 0 ? _a : [
        { label: 'Root', children: [{ label: 'Child A' }, { label: 'Child B' }] },
    ];
    var expanded = new Set((_b = state.expanded) !== null && _b !== void 0 ? _b : [0]);
    var selectedNode = String((_c = state.selectedNode) !== null && _c !== void 0 ? _c : '');
    var el = ctx.nodeElements.get(node.id);
    if (!el) {
        el = document.createElement('ul');
        el.id = node.id;
        el.className = 'lightdraw-tree';
        el.setAttribute('role', 'tree');
        parent.appendChild(el);
        bindDelegated(el, function (e) {
            var _a, _b, _c, _d;
            var leaf = e.target.closest('.lightdraw-tree-leaf');
            if (leaf) {
                var key = (_a = leaf.getAttribute('data-key')) !== null && _a !== void 0 ? _a : '';
                var st_1 = getState(node);
                node.metadata.componentState = __assign(__assign({}, st_1), { selectedNode: key });
                node.emit('select', syntheticEvent('select', node, { item: key, value: key }));
                (_b = node.getApp()) === null || _b === void 0 ? void 0 : _b.requestRender();
                return;
            }
            var btn = e.target.closest('.lightdraw-tree-toggle');
            if (!btn)
                return;
            var i = Number(btn.getAttribute('data-index'));
            var st = getState(node);
            var next = new Set((_c = st.expanded) !== null && _c !== void 0 ? _c : [0]);
            if (next.has(i))
                next.delete(i);
            else
                next.add(i);
            node.metadata.componentState = __assign(__assign({}, st), { expanded: Array.from(next), selectedNode: "p".concat(i) });
            node.emit('change', syntheticEvent('change', node, { value: i }));
            (_d = node.getApp()) === null || _d === void 0 ? void 0 : _d.requestRender();
        });
        ctx.nodeElements.set(node.id, el);
    }
    else if (el.parentElement !== parent) {
        parent.appendChild(el);
    }
    el.innerHTML = nodes
        .map(function (n, i) {
        var _a;
        var isOpen = expanded.has(i);
        var parentKey = "p".concat(i);
        var kids = isOpen && ((_a = n.children) === null || _a === void 0 ? void 0 : _a.length)
            ? "<ul class=\"lightdraw-tree-children\" role=\"group\">".concat(n.children
                .map(function (c, ci) {
                var key = "".concat(parentKey, ".c").concat(ci);
                return "<li role=\"none\"><button type=\"button\" class=\"lightdraw-tree-leaf".concat(selectedNode === key ? ' lightdraw-tree-leaf--selected' : '', "\" data-key=\"").concat(key, "\" role=\"treeitem\">").concat(escHtml(c.label), "</button></li>");
            })
                .join(''), "</ul>")
            : '';
        return "<li class=\"lightdraw-tree-node".concat(selectedNode === parentKey ? ' lightdraw-tree-node--selected' : '', "\" role=\"none\">\n        <button type=\"button\" class=\"lightdraw-tree-toggle\" data-index=\"").concat(i, "\" aria-label=\"Toggle ").concat(escHtml(n.label), "\" aria-expanded=\"").concat(isOpen, "\">\n          <span class=\"lightdraw-tree-chevron").concat(isOpen ? ' lightdraw-tree-chevron--open' : '', "\" aria-hidden=\"true\"></span>\n          <span class=\"lightdraw-tree-label\">").concat(escHtml(n.label), "</span>\n        </button>").concat(kids, "</li>");
    })
        .join('');
    var estHeight = 8;
    nodes.forEach(function (n, i) {
        estHeight += 28;
        if (expanded.has(i) && n.children)
            estHeight += n.children.length * 26;
    });
    var treeW = Number((_d = state.width) !== null && _d !== void 0 ? _d : 220);
    el.style.cssText = absPosition(node, treeW, estHeight);
    ctx.applyA11y(node, el);
    ctx.applyUiClasses(node, el);
    ctx.seenIds.add(node.id);
}
